import { HexColorPicker } from 'react-colorful';
import { EyeDropper } from 'react-eyedrop';
import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ButtonIcon } from '../button-icon';
import { IconEyeDropper } from '../../icons';
import { useClickOutside } from '../../hooks/use-click-outside';
import { Text } from '../text'
import { Input } from '../input';
import { useDebounce } from '../../hooks/use-debounce';
import { Button } from '../button';
import useEyeDropper from 'use-eye-dropper'

export interface ColorPickerProps {
    color: string;
    setColor: (color: string) => void;
    recentColorValues?: string[];
    setRecentColors?: (colors: string[]) => void;
    maxRecentColors?: number;
    withHexInput?: boolean;
    withEyeDropper?: boolean;
    suggestedColors?: string[];
}

const hexReg = /^#([0-9A-F]{3}){1,2}$/i

export const ColorPicker: React.FC<ColorPickerProps> = (props) => {
    const {
        color,
        setColor,
        recentColorValues,
        setRecentColors,
        maxRecentColors = 6,
        withHexInput = false,
        withEyeDropper = false,
        suggestedColors
    } = props;

    const [isOpen, setIsOpen] = useState(false);
    const [hex, setHex] = useState(color);
    const popover = useRef(null);
    const close = useCallback(() => {
            setIsOpen(false);
    },[]);
    useClickOutside(popover, close);
    const eyeDropper = useEyeDropper(); // currently only supported in chromium browsers
    const pickColor = useCallback(() => {
        const openPicker = async () => {
            try {
                const color = await eyeDropper.open();
                setColor(color.sRGBHex);
                setHex(color.sRGBHex);
            } catch (e) {}
        }
        openPicker()
    }, [eyeDropper])

    const debouncedSetColor = useDebounce((newColor: string) => {
        if (hexReg.test(newColor)) {
            setColor(newColor);
        }
    }, 300);

    const handleHexChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.currentTarget.value;
        const newValue = value.includes('#') ? value : '#'.concat(value);
        setHex(newValue);
        debouncedSetColor(newValue);
    };

    /**
     *  Update recent colors list after color state changes
     */
    useEffect(() => {
        if (recentColorValues && setRecentColors) {
            if (!recentColorValues.includes(color)) {
                setRecentColors([color, ...recentColorValues].slice(0, maxRecentColors));
            }
        }
    }, [color, recentColorValues, maxRecentColors, setRecentColors]);

    return (
        <div className="picker">
            <Button
                className="hover:primary-hover-state active:primary-active-state"
                style={{
                    backgroundColor: color,
                    width: '16px',
                    height: '16px',
                    display: 'flex',
                    borderRadius: '4px',
                    border: '1px solid var(--sys-primary, #101010)'
                }}
                color={"ghost"}
                size={"sm"}
                onClick={() => setIsOpen(true)}
            />

            {isOpen && (
                <div
                    className="flex w-[280px] flex-col items-start gap-4 rounded-lg bg-[var(--sys-surface,_#FFF)] p-4 shadow-lg"
                    ref={popover}
                >
                    <div className="w-full">
                        <HexColorPicker color={color} onChange={(newColor) => {
                                debouncedSetColor(newColor);
                                setHex(newColor);
                        }} />
                    </div>

                    <div className="flex w-full items-end gap-4">
                        {withEyeDropper && eyeDropper.isSupported() ? (<ButtonIcon
                            onClick={(event) => {
                                pickColor();
                            }}
                            color={'ghost'}
                            size={'md'}
                        >
                            <IconEyeDropper />
                        </ButtonIcon>) : (
                            <EyeDropper // fallback for unsupported browsers, poorer functionality
                                customComponent={({ onClick }) => (
                                    <ButtonIcon
                                        onClick={(event) => {
                                            onClick(event);
                                        }}
                                        color={'ghost'}
                                        size={'md'}
                                    >
                                        <IconEyeDropper />
                                    </ButtonIcon>
                                )}
                                onChange={(c) => {
                                    setColor(c.hex);
                                    setHex(c.hex);
                                }}
                            />

                        )}
                        <div className="h-12 w-12 rounded-full" style={{ backgroundColor: color, boxShadow: '0px 0px 0px 2px var(--sys-primary-container, #848484)' }} />
                        {withHexInput && (
                            <div className="flex flex-1 flex-col items-start gap-1 -mb-2">
                                <Text variant="label-large">HEX</Text>
                                <Input onChange={(event) => handleHexChange(event)} value={hex} />
                            </div>
                        )}
                    </div>

                    {recentColorValues && (
                        <div className="mt-4">
                            <Text variant="label-large">Recently Used</Text>
                            <div className="mt-2 flex gap-2">
                                {recentColorValues.map((c) => (
                                    <div
                                        key={c}
                                        className="hover:primary-hover-state active:primary-active-state"
                                        onClick={() => {
                                                setColor(c);
                                                setHex(c);
                                        }}
                                        style={{
                                            background: c,
                                            width: 24,
                                            height: 24,
                                            borderRadius: '100px',
                                            border: '2px solid #FFF',
                                            boxShadow: c === color ? '0px 0px 0px 2px var(--sys-outline, #848484)' : "unset",
                                            cursor: 'pointer',
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {suggestedColors && (
                        <div className="mt-4">
                            <Text variant="label-large">Suggested</Text>
                            <div className="mt-2 flex gap-2">
                                {suggestedColors.map((c) => (
                                    <div
                                        key={c}
                                        className="hover:primary-hover-state active:primary-active-state"
                                        onClick={() => {
                                                setColor(c);
                                                setHex(c);
                                        }}
                                        style={{
                                            background: c,
                                            width: 24,
                                            height: 24,
                                            borderRadius: '100px',
                                            border: '2px solid #FFF',
                                            cursor: 'pointer',
                                        }}
                                    />
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
